import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ThemedButton } from '../ThemedButton';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Shield, Heart, CheckCircle2 } from 'lucide-react';
import { useWizard } from '../../contexts/WizardContext';

const CoveragePackage = () => {
  const { formData, updateFormData, nextStep } = useWizard();
  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      gl_limit_option: formData.gl_limit_per_occurrence ? `${formData.gl_limit_per_occurrence}/${formData.gl_limit_aggregate}` : '',
      requested_optional_coverages: formData.requested_optional_coverages || [],
    }
  });

  const requestedOptionalCoverages = watch('requested_optional_coverages');

  const onSubmit = (data) => {
    // Parse the gl_limit_option to extract per_occurrence and aggregate
    const [per_occurrence, aggregate] = data.gl_limit_option.split('/');
    
    updateFormData({
      gl_limit_per_occurrence: per_occurrence,
      gl_limit_aggregate: aggregate,
      requested_optional_coverages: data.requested_optional_coverages,
      // Always include participant accident as part of the package
      participant_accident_limit: '$25,000',
      gl_coverage: true,
      accident_coverage: true
    });
    nextStep();
  };

  const toggleOptionalCoverage = (coverage) => {
    const current = requestedOptionalCoverages || [];
    if (current.includes(coverage)) {
      setValue('requested_optional_coverages', current.filter(c => c !== coverage));
    } else {
      setValue('requested_optional_coverages', [...current, coverage]);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Package Header */}
      <div className="bg-[#EEF0FF] border border-[#7B6FE8]/20 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Badge className="bg-[#7B6FE8] text-white border-0">
            Coverage Package
          </Badge>
        </div>
        <p className="text-sm text-slate-700">
          Your coverage includes <strong>General Liability</strong> and <strong>Participant Accident</strong> together as a complete protection package.
        </p>
      </div>

      {/* General Liability Section */}
      <div className="border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="h-5 w-5 text-purple-500" />
          <h3 className="font-semibold text-slate-900">General Liability Coverage</h3>
          <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />
          <span className="text-xs text-green-600 font-medium">Included</span>
        </div>
        
        <Label className="text-sm mb-3 block text-slate-700">Select your preferred coverage limit *</Label>
        
        <Controller
          name="gl_limit_option"
          control={control}
          rules={{ required: 'Please select a coverage limit' }}
          render={({ field }) => (
            <RadioGroup
              onValueChange={field.onChange}
              defaultValue={field.value}
              className="space-y-3"
            >
              <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg hover:border-purple-300 hover:bg-purple-50/50 transition-colors">
                <RadioGroupItem value="$1,000,000/$3,000,000" id="limit_1m" data-testid="gl-limit-1m" />
                <Label htmlFor="limit_1m" className="font-normal cursor-pointer flex-1">
                  <span className="font-semibold text-slate-900">$1,000,000</span>
                  <span className="text-slate-600"> per occurrence / </span>
                  <span className="font-semibold text-slate-900">$3,000,000</span>
                  <span className="text-slate-600"> aggregate</span>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg hover:border-purple-300 hover:bg-purple-50/50 transition-colors">
                <RadioGroupItem value="$2,000,000/$3,000,000" id="limit_2m" data-testid="gl-limit-2m" />
                <Label htmlFor="limit_2m" className="font-normal cursor-pointer flex-1">
                  <span className="font-semibold text-slate-900">$2,000,000</span>
                  <span className="text-slate-600"> per occurrence / </span>
                  <span className="font-semibold text-slate-900">$3,000,000</span>
                  <span className="text-slate-600"> aggregate</span>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg hover:border-purple-300 hover:bg-purple-50/50 transition-colors">
                <RadioGroupItem value="$3,000,000/$3,000,000" id="limit_3m" data-testid="gl-limit-3m" />
                <Label htmlFor="limit_3m" className="font-normal cursor-pointer flex-1">
                  <span className="font-semibold text-slate-900">$3,000,000</span>
                  <span className="text-slate-600"> per occurrence / </span>
                  <span className="font-semibold text-slate-900">$3,000,000</span>
                  <span className="text-slate-600"> aggregate</span>
                </Label>
              </div>
            </RadioGroup>
          )}
        />
        {errors.gl_limit_option && (
          <p className="text-sm text-red-600 mt-2">{errors.gl_limit_option.message}</p>
        )}
      </div>

      {/* Participant Accident Section */}
      <div className="border border-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="h-5 w-5 text-pink-500" />
          <h3 className="font-semibold text-slate-900">Participant Accident Coverage</h3>
          <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />
          <span className="text-xs text-green-600 font-medium">Included</span>
        </div>
        
        <p className="text-sm text-slate-600 mb-4">
          Medical coverage for injuries sustained during practices, games, and travel.
        </p>
        
        <div className="bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Coverage Limit</p>
              <p className="text-xs text-slate-500 mt-0.5">per participant</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900">$25,000</p>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 mt-1">
                Included in Package
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Optional Coverages Section */}
      <div className="border border-slate-200 rounded-xl p-5">
        <Label className="text-base mb-2 block font-semibold text-slate-900">Need for Any Other Coverages?</Label>
        <p className="text-sm text-slate-600 mb-4">
          Select any additional coverages you're interested in:
        </p>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg hover:border-purple-300 hover:bg-purple-50/50 transition-colors">
            <Checkbox
              id="do_coverage"
              data-testid="optional-do-checkbox"
              checked={requestedOptionalCoverages?.includes('Directors & Officers')}
              onCheckedChange={() => toggleOptionalCoverage('Directors & Officers')}
            />
            <Label htmlFor="do_coverage" className="font-normal cursor-pointer flex-1">
              Directors & Officers (D&O)
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg hover:border-purple-300 hover:bg-purple-50/50 transition-colors">
            <Checkbox
              id="cyber_coverage"
              data-testid="optional-cyber-checkbox"
              checked={requestedOptionalCoverages?.includes('Cyber')}
              onCheckedChange={() => toggleOptionalCoverage('Cyber')}
            />
            <Label htmlFor="cyber_coverage" className="font-normal cursor-pointer flex-1">
              Cyber Liability
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg hover:border-purple-300 hover:bg-purple-50/50 transition-colors">
            <Checkbox
              id="equipment_property_coverage"
              data-testid="optional-equipment-checkbox"
              checked={requestedOptionalCoverages?.includes('Equipment & Property')}
              onCheckedChange={() => toggleOptionalCoverage('Equipment & Property')}
            />
            <Label htmlFor="equipment_property_coverage" className="font-normal cursor-pointer flex-1">
              Equipment & Property
            </Label>
          </div>

          <div className="flex items-center space-x-3 p-3 border border-slate-200 rounded-lg hover:border-purple-300 hover:bg-purple-50/50 transition-colors">
            <Checkbox
              id="workers_comp_coverage"
              data-testid="optional-workers-comp-checkbox"
              checked={requestedOptionalCoverages?.includes('Workers Compensation')}
              onCheckedChange={() => toggleOptionalCoverage('Workers Compensation')}
            />
            <Label htmlFor="workers_comp_coverage" className="font-normal cursor-pointer flex-1">
              Workers Compensation
            </Label>
          </div>
        </div>
      </div>

      <ThemedButton 
        type="submit"
        data-testid="coverage-package-next-btn"
        variant="wizard" size="full"
      >
        Next: Claims History
      </ThemedButton>
    </form>
  );
};

export default CoveragePackage;
